import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Scissors, Sparkles, Heart, Star, Leaf, ArrowRight, Calendar, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const BOOKING_URL = "https://home.shortcutssoftware.com/ah#/_h/sites/6509/details";
const BOOKING_URL_BEAUTY = "https://home.shortcutssoftware.com/ah#/_h/sites/6509/details";
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Team data from the website
const team = [
  { name: 'Paige', role: 'Executive Director', image: 'https://customer-assets.emergentagent.com/job_6b6596b0-1b37-4378-8699-89c151293ef2/artifacts/ibif2mlj_PAIGE.png' },
  { name: 'Josie', role: 'Creative Director', image: 'https://customer-assets.emergentagent.com/job_d95e4940-e0b0-4d59-9b4c-c960d9d6604f/artifacts/8u4momme_PXL_20260227_044259811.RAW-01.COVER.jpg' },
  { name: 'Deana', role: 'Executive Beauty Director', image: 'https://customer-assets.emergentagent.com/job_6b6596b0-1b37-4378-8699-89c151293ef2/artifacts/wuvji5lc_DEANA.png' },
  { name: 'Graham', role: 'Principle Stylist', image: 'https://customer-assets.emergentagent.com/job_6b6596b0-1b37-4378-8699-89c151293ef2/artifacts/lmd0w4nw_Untitled%20design%20%285%29.png' },
  { name: 'Michelle', role: 'Senior Stylist', image: 'https://customer-assets.emergentagent.com/job_6b6596b0-1b37-4378-8699-89c151293ef2/artifacts/6zjft2kx_MICHELLE.png' },
  { name: 'Coming Soon', role: 'Emerging Stylist', image: null },
];

const reviews = [
  { 
    text: "Lovely Salon. Have been 3 times and so happy with my hair each time.",
    author: "Mandy McArthur",
    source: "Google Reviews",
    rating: 5
  },
  { 
    text: "This salon is truly beautiful and the women working there are so friendly and professional! I never leave feeling disappointed..ever.",
    author: "Natalie Cook",
    source: "Google Reviews",
    rating: 5
  },
  { 
    text: "Love Michelle, she's amazing! Love the salon and today was just an extra bit special. Had a great time x",
    author: "Carly-Jane",
    source: "My Local Salon",
    rating: 5
  },
];

const comingSoon = [
  'Full Body Red Light Therapy',
  'Cosmetic Nurse',
  'Eyebrow Tattooing',
  'Tattoo Removal',
];

export default function Home() {
  // Coming Soon Form State
  const [comingSoonForm, setComingSoonForm] = useState({ email: '', selectedServices: [] });
  const [comingSoonSubmitting, setComingSoonSubmitting] = useState(false);

  // Handle Coming Soon Form
  const handleComingSoonSubmit = async (e) => {
    e.preventDefault();
    setComingSoonSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/interest/coming-soon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: comingSoonForm.email, 
          services: comingSoonForm.selectedServices 
        }),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || "Thanks! We'll notify you when these services launch.");
        setComingSoonForm({ email: '', selectedServices: [] });
      } else {
        toast.error(data.detail || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      toast.error('Unable to submit. Please try again.');
    } finally {
      setComingSoonSubmitting(false);
    }
  };

  // Toggle service selection for Coming Soon form
  const toggleService = (service) => {
    setComingSoonForm(prev => ({
      ...prev,
      selectedServices: prev.selectedServices.includes(service)
        ? prev.selectedServices.filter(s => s !== service)
        : [...prev.selectedServices, service]
    }));
  };

  return (
    <main className="min-h-screen bg-[#FFFDF9]" data-testid="home-page">
      <SEO page="home" />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_1dc100c1-fd6e-4e96-8e6a-366ccfb0323b/artifacts/q7ymi8hl_Beauty-Hair-Dekoset-0425-09-4-5-sRGB-1025-824x1030.jpg"
            alt="Artistry House - Hair, Beauty & Wellness"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FFFDF9] via-[#FFFDF9]/90 to-transparent" />
        </div>

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 py-32 md:py-40">
          <div className="max-w-2xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-[#D4C4B0]/20 border border-[#D4C4B0]/50 rounded-full px-4 py-2 mb-8 animate-fade-in-up">
              <Leaf className="w-4 h-4 text-[#8D7B68]" />
              <span className="text-sm text-[#5D4A3A]">100% Sustainable Salon</span>
            </div>

            {/* Headline */}
            <h1 className="text-5xl md:text-7xl font-medium text-[#2C2420] leading-[1.1] mb-6 animate-fade-in-up animation-delay-100">
              Artistry House: Premium Hair & Beauty in Camberwell
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-[#2C2420] leading-relaxed mb-8 animate-fade-in-up animation-delay-200">
              Welcome to Artistry House — where hair, beauty, and wellness converge 
              in the heart of Camberwell. Established in 1978.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-300">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button 
                  className="bg-[#8D7B68] hover:bg-[#6B5D4D] text-white rounded-full px-8 py-6 text-lg font-medium transition-all hover:-translate-y-1"
                  data-testid="hero-book-hair-btn"
                >
                  <Scissors className="w-5 h-5 mr-2" />
                  Book Hair
                </Button>
              </a>
              <a href={BOOKING_URL_BEAUTY} target="_blank" rel="noopener noreferrer">
                <Button 
                  className="bg-[#4A5D44] hover:bg-[#3d4e38] text-white rounded-full px-8 py-6 text-lg font-medium transition-all hover:-translate-y-1"
                  data-testid="hero-book-beauty-btn"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Book Beauty & Wellness
                </Button>
              </a>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-[#3D352D]/30 rounded-full flex justify-center">
            <div className="w-1.5 h-3 bg-[#8D7B68] rounded-full mt-2" />
          </div>
        </div>
      </section>

      {/* Waterfall Head Spa Showcase */}
      <section className="relative overflow-hidden" data-testid="waterfall-spa-showcase">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[600px]">
          {/* Image Side */}
          <div className="relative h-[400px] lg:h-auto">
            <img
              src="https://customer-assets.emergentagent.com/job_1dc100c1-fd6e-4e96-8e6a-366ccfb0323b/artifacts/fmf8xi6j_head%20massage.jpg"
              alt="Waterfall Head Spa luxury treatment"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2C2420]/40 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-[#2C2420]/20" />
          </div>

          {/* Content Side */}
          <div className="bg-[#2C2420] flex items-center px-8 md:px-16 lg:px-20 py-16 lg:py-24">
            <div className="max-w-lg">
              <p className="text-sm uppercase tracking-[0.3em] text-[#D4C4B0] mb-4">Signature Experience</p>
              <h2 className="text-4xl md:text-5xl font-medium text-white leading-tight mb-4">
                Waterfall Head Spa
              </h2>
              <p className="text-xl text-[#D4C4B0] italic mb-6">
                More than relaxation — it's a scalp remedy.
              </p>
              <p className="text-base text-[#BCAAA4] leading-relaxed mb-4">
                Warm rain-curtain therapy cascades over your scalp as tension melts away. But this isn't just about feeling good — our targeted scalp protocols address real concerns like hair loss, thinning, oily roots, dandruff, and dry, sensitive scalps.
              </p>
              <p className="text-base text-[#BCAAA4] leading-relaxed mb-4">
                Every session includes a deep scalp analysis, detox cleansing, and a customised treatment using clinical-grade serums. Finished with a luxury blowwave so you leave looking as incredible as you feel.
              </p>
              <p className="text-sm text-[#D4C4B0] font-medium mb-8">
                Scalp thinning? Oily roots? Flaking? We have a protocol for that.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="https://home.shortcutssoftware.com/ah#/_h/sites/6509/details" target="_blank" rel="noopener noreferrer">
                  <Button className="bg-[#8D7B68] hover:bg-[#6B5D4D] text-white rounded-full px-8 py-4 text-base w-full sm:w-auto" data-testid="spa-showcase-book-btn">
                    <Calendar className="w-5 h-5 mr-2" />
                    Book Now
                  </Button>
                </a>
                <a href="tel:0398826969">
                  <Button variant="outline" className="border-[#D4C4B0] text-[#D4C4B0] hover:bg-[#D4C4B0] hover:text-[#2C2420] rounded-full px-8 py-4 text-base w-full sm:w-auto" data-testid="spa-showcase-call-btn">
                    <Phone className="w-5 h-5 mr-2" />
                    Call (03) 9882 6969
                  </Button>
                </a>
              </div>
              <div className="mt-8">
                <Link to="/waterfall-head-spa" className="inline-flex items-center gap-2 text-sm text-[#D4C4B0] hover:text-white transition-colors">
                  Discover the full experience <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ground Floor - Hair Section */}
      <section className="py-24 md:py-32 bg-[#D4C4B0]/20" data-testid="hair-intro-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div className="relative">
              <div className="aspect-[4/5] rounded-lg overflow-hidden">
                <img 
                  src="/images/hair_ground.jpg"
                  alt="Artistry House hair salon ground floor"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Floating card */}
              <div className="absolute -bottom-6 -right-6 md:bottom-8 md:right-8 bg-white p-6 rounded-lg shadow-lg max-w-xs">
                <div className="flex items-center gap-3 mb-2">
                  <Scissors className="w-6 h-6 text-[#8D7B68]" />
                  <span className="text-sm text-[#8D7B68] font-medium">Ground Floor</span>
                </div>
                <h4 className="text-xl font-medium text-[#2C2420]">Hair Artistry</h4>
              </div>
            </div>

            {/* Content */}
            <div>
              <p className="text-sm uppercase tracking-widest text-[#8D7B68] mb-4">Since 1978</p>
              <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">
                Ground Floor;<br />Hair Artistry
              </h2>
              <p className="text-lg text-[#5D4A3A] leading-relaxed mb-6">
                Established in 1978 in Camberwell, the team at Artistry House are dedicated to providing a first-class service using luxurious quality products. Our success stems from a committed and passionate team of hairdressers who continually develop new skills and knowledge.
              </p>
              <p className="text-[#5D4A3A] leading-relaxed mb-8">
                We can be found in the 'service precinct' at the northern end of Burke Road Camberwell. Feel free to drop in to discuss your needs with one of our friendly hairdressers.
              </p>
              <Link to="/hair">
                <Button className="bg-[#8D7B68] hover:bg-[#6B5D4D] text-white rounded-full px-8 py-4">
                  Explore Hair Services
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 1st Floor - Beauty Section */}
      <section className="py-24 md:py-32" data-testid="beauty-intro-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <div className="order-2 lg:order-1">
              <p className="text-sm uppercase tracking-widest text-[#8D7B68] mb-4">15+ Years Experience</p>
              <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">
                1st Floor;<br />Beauty & Wellness
              </h2>
              <p className="text-lg text-[#5D4A3A] leading-relaxed mb-6">
                At Artistry House, we believe beauty is a blend of science, art, and relaxation. With over 15 years of professional experience, our mission is to provide transformative results in a luxurious, welcoming environment.
              </p>
              <p className="text-[#5D4A3A] leading-relaxed mb-8">
                Whether you are looking for advanced clinical skin revision or a moment of pure serenity, our expert team is here to add value to your life and confidence to your skin.
              </p>
              <Link to="/beauty">
                <Button className="bg-[#8D7B68] hover:bg-[#6B5D4D] text-white rounded-full px-8 py-4">
                  Explore Beauty Services
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>

            {/* Image */}
            <div className="relative order-1 lg:order-2">
              <div className="aspect-[4/5] rounded-lg overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=800&h=1000&fit=crop"
                  alt="Beauty treatment"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Floating card */}
              <div className="absolute -bottom-6 -left-6 md:bottom-8 md:left-8 bg-white p-6 rounded-lg shadow-lg max-w-xs">
                <div className="flex items-center gap-3 mb-2">
                  <Sparkles className="w-6 h-6 text-[#8D7B68]" />
                  <span className="text-sm text-[#8D7B68] font-medium">1st Floor</span>
                </div>
                <h4 className="text-xl font-medium text-[#2C2420]">Beauty & Wellness</h4>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 md:py-32 bg-[#D4C4B0]/20" data-testid="team-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          {/* Section Header */}
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-[#8D7B68] mb-4">Meet The Experts</p>
            <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">Our Team</h2>
            <div className="section-divider bg-[#D4C4B0]" />
          </div>

          {/* Team Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {team.map((member) => (
              <div 
                key={member.name}
                className="text-center"
                data-testid={`team-member-${member.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="aspect-[3/4] rounded-lg overflow-hidden mb-4">
                  {member.image ? (
                    <img 
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-[#E8DED2] flex items-center justify-center">
                      <div className="text-center px-4">
                        <Sparkles className="w-8 h-8 text-[#8D7B68] mx-auto mb-3" />
                        <p className="text-sm font-medium text-[#8D7B68] uppercase tracking-widest">Coming Soon</p>
                      </div>
                    </div>
                  )}
                </div>
                <h4 className="text-lg font-medium text-[#2C2420]">{member.image ? member.name : 'Coming Soon'}</h4>
                <p className="text-sm text-[#8D7B68]">{member.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sustainability Section */}
      <section className="py-24 md:py-32 bg-[#E8DED2]/50" data-testid="sustainability-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#4A5D44] text-white rounded-full px-4 py-2 mb-6">
                <Leaf className="w-4 h-4" />
                <span className="text-sm font-medium">Sustainable Salon</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">
                We Are a<br />"Sustainable Salon"
              </h2>
              <p className="text-lg text-[#5D4A3A] leading-relaxed mb-6">
                We are proud to announce that Artistry House has partnered up with Sustainable Salons — Australia's #1 salon recycling service.
              </p>
              <p className="text-[#5D4A3A] leading-relaxed mb-8">
                Sustainable Salons supports our ongoing commitment to reduce our ecological footprint, achieve zero waste, support charitable causes and implement environmentally sustainable practices in our work. <strong>95% of the resources</strong> used during your service will be rescued from landfill and sent for repurposing.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/sustainability">
                  <Button className="bg-[#4A5D44] hover:bg-[#3d4e38] text-white rounded-full px-6">
                    Learn More About Our Commitment
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a 
                  href="https://sustainablesalons.org" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#4A5D44] font-medium hover:underline px-2"
                >
                  Visit Sustainable Salons
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=600&h=500&fit=crop"
                alt="Sustainability"
                className="w-full rounded-lg"
              />
            </div>
          </div>

          {/* Ponytail Donation */}
          <div className="mt-20 p-8 md:p-12 bg-white rounded-lg">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="w-20 h-20 rounded-full bg-[#D4C4B0]/30 flex items-center justify-center flex-shrink-0">
                <Heart className="w-10 h-10 text-[#8D7B68]" />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-medium text-[#2C2420] mb-3">Ponytail Donation Program</h3>
                <p className="text-[#5D4A3A]">
                  Have you got what it takes to be a Ponytail Superstar? We're part of Australia's biggest ponytail donation program, creating medical wigs for people in need.
                </p>
              </div>
              <Link to="/sustainability">
                <Button variant="outline" className="border-[#8D7B68] text-[#8D7B68] hover:bg-[#8D7B68] hover:text-white rounded-full px-6 whitespace-nowrap">
                  Learn More
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-24 md:py-32" data-testid="reviews-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          {/* Section Header */}
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-[#8D7B68] mb-4">What Our Clients Say</p>
            <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">Client Reviews</h2>
            <div className="section-divider bg-[#D4C4B0]" />
          </div>

          {/* Rating Badges */}
          <div className="flex flex-wrap justify-center gap-8 mb-12">
            <div className="text-center">
              <p className="text-sm text-[#5D4A3A] mb-2">Google Reviews</p>
              <div className="flex items-center gap-2">
                <span className="text-3xl font-medium text-[#2C2420]">4.5</span>
                <span className="text-[#5D4A3A]">/ 5</span>
              </div>
            </div>
            <div className="text-center">
              <p className="text-sm text-[#5D4A3A] mb-2">My Local Salon</p>
              <div className="flex items-center gap-2">
                <span className="text-3xl font-medium text-[#2C2420]">5</span>
                <span className="text-[#5D4A3A]">/ 5</span>
              </div>
            </div>
            <div className="text-center">
              <p className="text-sm text-[#5D4A3A] mb-2">Facebook</p>
              <div className="flex items-center gap-2">
                <span className="text-3xl font-medium text-[#2C2420]">5</span>
                <span className="text-[#5D4A3A]">/ 5</span>
              </div>
            </div>
          </div>

          {/* Reviews Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {reviews.map((review, index) => (
              <div 
                key={index}
                className="review-card bg-white p-8 rounded-lg border border-[#D4C4B0]/50"
                data-testid={`review-${index}`}
              >
                {/* Stars */}
                <div className="flex gap-1 mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-[#D4C4B0] text-[#D4C4B0]" />
                  ))}
                </div>
                <p className="text-[#5D4A3A] italic mb-6 leading-relaxed">"{review.text}"</p>
                <div>
                  <p className="font-medium text-[#2C2420]">{review.author}</p>
                  <p className="text-sm text-[#6B5D4D]">{review.source}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coming Soon Section */}
      <section className="py-24 md:py-32 bg-[#2C2420]" data-testid="coming-soon-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-12">
            <p className="text-sm uppercase tracking-widest text-[#D4C4B0] mb-4">Winter 2026</p>
            <h2 className="text-4xl md:text-5xl font-medium text-white mb-6">Coming Soon</h2>
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {comingSoon.map((service) => (
                <div 
                  key={service}
                  className="bg-[#3D352D] px-6 py-3 rounded-full text-white"
                >
                  {service}
                </div>
              ))}
            </div>
          </div>
          
          {/* Be First to Know Form */}
          <div className="max-w-xl mx-auto bg-[#3D352D] p-8 rounded-lg">
            <h3 className="text-xl font-medium text-white mb-2 text-center">Be The First To Know</h3>
            <p className="text-[#BCAAA4] text-center mb-6">Sign up to get notified when these services launch</p>
            <form className="space-y-4" data-testid="coming-soon-form" onSubmit={handleComingSoonSubmit}>
              <input 
                type="email" 
                placeholder="Your Email" 
                required
                value={comingSoonForm.email}
                onChange={(e) => setComingSoonForm(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-4 py-3 rounded-lg bg-[#2C2420] border border-[#5D4A3A] text-white placeholder-[#8D7B68] focus:outline-none focus:border-[#D4C4B0]"
              />
              <div className="text-[#BCAAA4] text-sm">
                <p className="mb-2">I'm interested in:</p>
                <div className="flex flex-wrap gap-3">
                  {comingSoon.map((service) => (
                    <label key={service} className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={comingSoonForm.selectedServices.includes(service)}
                        onChange={() => toggleService(service)}
                        className="rounded border-[#5D4A3A] bg-[#2C2420] text-[#8D7B68]" 
                      />
                      <span>{service}</span>
                    </label>
                  ))}
                </div>
              </div>
              <Button 
                type="submit" 
                disabled={comingSoonSubmitting}
                className="w-full bg-[#8D7B68] hover:bg-[#6B5D4D] text-white rounded-full py-4 disabled:opacity-50"
              >
                {comingSoonSubmitting ? 'Submitting...' : 'Notify Me'}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32" data-testid="cta-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">
            Ready to Experience<br />Artistry House?
          </h2>
          <p className="text-lg text-[#5D4A3A] max-w-2xl mx-auto mb-8">
            Book your appointment today and discover why we're Camberwell's premier destination for hair, beauty, and wellness.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              <Button 
                className="bg-[#8D7B68] hover:bg-[#6B5D4D] text-white rounded-full px-10 py-6 text-lg font-medium transition-all hover:-translate-y-1"
                data-testid="cta-book-hair-btn"
              >
                <Scissors className="w-5 h-5 mr-2" />
                Book Hair
              </Button>
            </a>
            <a href={BOOKING_URL_BEAUTY} target="_blank" rel="noopener noreferrer">
              <Button 
                className="bg-[#4A5D44] hover:bg-[#3d4e38] text-white rounded-full px-10 py-6 text-lg font-medium transition-all hover:-translate-y-1"
                data-testid="cta-book-beauty-btn"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Book Beauty & Wellness
              </Button>
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
